import groupBy from 'lodash.groupby';

import { createSlice } from '@reduxjs/toolkit';

import {
  getAgreementDetailsThunk,
  getAgreementsThunk,
} from '../../services/agreementService/agreementThunk';
import {
  getIslamicPendingSigningAgreementsThunk,
  getIslamicSignedAgreementsThunk,
  getIslamicPendingEStampAgreementsThunk,
  getIslamicInProgressEStampAgreementsThunk,
  getIslamicUploadedEStampAgreementsThunk,
  getPendingTradingAgreementsThunk,
} from '../../services/islamicAgreementService/islamicAgreementThunk';
import { getFilesByEntityIdThunk } from '../../services/fileService/fileThunk';
import { UploadedFile } from '../application/applicationSlice';
import { IslamicApplication } from '../islamicApplication/islamicApplicationSlice';
import { Attestation } from '../attestation/attestationSlice';

export type IslamicAgreement = {
  id: string;
  application?: IslamicApplication;
  applicationid?: string;
  attestationid?: string;
  attestation?: Attestation;
  agreementStepStatusid?: string;
  agreementNo?: string;
  clickedDownloadEStampingCert?: boolean;
  agreementStatus:
    | 'PENDING'
    | 'DONE_VIDEO_ATTESTATION'
    | 'DONE_LIVE_ATTESTATION'
    | 'REQUESTED_FOR_LIVE_ATTESTATION'
    | 'PENDING_SIGNING'
    | 'PENDING_DISBURSEMENT'
    | string;
  loanTenure?: number;
  lastinstalmentAmount?: number;
  monthlyInstalmentAmount?: number;
  loanPayableAmount?: number;
  disbursementAmount?: number;
  signDate?: string;
  repaymentOption?: string;
  stampingFee?: number;
  attestationFee?: number;
  processingFee?: number;
  loanInterestRate?: number;
  firstInstalmentDate?: string;
  lastInstalmentDate?: string;
  monthlyInstalmentDay?: string;
  lastInstalmentAmount?: string;
  signedAgreementFileId?: string;
  protectedSignedAgreementFileId?: string;
  disbursementPostedDate?: string;
  eStampingCertificateFileId?: string;
  approveUploadedEStampingCert?: boolean;
  approveEStampingCertDate?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type AgreementState = {
  agreementList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      data: IslamicAgreement[];
    };
  };
  pendingSigningIslamicAgreements: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      searchTotal: number;
      data: IslamicAgreement[];
    };
  };
  signedIslamicAgreements: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      searchTotal: number;
      data: IslamicAgreement[];
    };
  };
  pendingEStampIslamicAgreements: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      searchTotal: number;
      data: IslamicAgreement[];
    };
  };
  inProgressEStampIslamicAgreements: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      searchTotal: number;
      data: IslamicAgreement[];
    };
  };
  uploadedEStampIslamicAgreements: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      searchTotal: number;
      data: IslamicAgreement[];
    };
  };
  agreementDetails: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: IslamicAgreement;
  };
  agreementFiles: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      protectedsignedagreementpdf?: UploadedFile[];
      signedagreementpdf?: UploadedFile[];
      estamping?: UploadedFile[];
    };
  };
  pendingTradingAgreements: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: {
      total: number;
      currentPage: number;
      pageSize: number;
      data: IslamicAgreement[];
    };
  };
};

const initialState: AgreementState = {
  agreementList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  pendingSigningIslamicAgreements: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      searchTotal: 0,
      data: [],
    },
  },
  signedIslamicAgreements: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      searchTotal: 0,
      data: [],
    },
  },
  pendingEStampIslamicAgreements: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      searchTotal: 0,
      data: [],
    },
  },
  inProgressEStampIslamicAgreements: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      searchTotal: 0,
      data: [],
    },
  },
  uploadedEStampIslamicAgreements: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      searchTotal: 0,
      data: [],
    },
  },
  agreementDetails: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: undefined,
  },
  agreementFiles: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      protectedsignedagreementpdf: [],
      signedagreementpdf: [],
      estamping: [],
    },
  },
  pendingTradingAgreements: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
};

export const islamicAgreementSlice = createSlice({
  name: 'islamicAgreement',
  initialState,
  reducers: {
    setAgreementDetails: (state, action) => {
      state.agreementDetails.data = action.payload;
    },
    agreementDetailsStateReset: (state) => {
      state.agreementDetails = initialState.agreementDetails;
      state.agreementFiles = initialState.agreementFiles;
    },
    agreementStateReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAgreementsThunk.pending, (state) => {
        state.agreementList.isLoading = true;
      })
      .addCase(getAgreementsThunk.fulfilled, (state, action) => {
        state.agreementList.isLoading = false;
        state.agreementList.data = {
          ...action.payload,
          data: action.payload.data.map((agreement: IslamicAgreement) => ({
            ...agreement,
            agreementStatus:
              agreement.agreementStatus === 'PENDING'
                ? 'PENDING_FOR_ATTESTATION'
                : agreement.agreementStatus,
          })),
        };
      })
      .addCase(getAgreementsThunk.rejected, (state, action) => {
        state.agreementList.isLoading = false;
        state.agreementList.isError = true;
        state.agreementList.errorMessage = action.payload;
      });

    builder
      .addCase(getIslamicPendingSigningAgreementsThunk.pending, (state) => {
        state.pendingSigningIslamicAgreements.isLoading = true;
      })
      .addCase(getIslamicPendingSigningAgreementsThunk.fulfilled, (state, action) => {
        state.pendingSigningIslamicAgreements.isLoading = false;
        state.pendingSigningIslamicAgreements.data = {
          ...action.payload,
          data: action.payload.data.map((agreement: IslamicAgreement) => ({
            ...agreement,
            agreementStatus:
              agreement.agreementStatus === 'PENDING'
                ? 'PENDING_FOR_ATTESTATION'
                : agreement.agreementStatus,
          })),
        };
      })
      .addCase(getIslamicPendingSigningAgreementsThunk.rejected, (state, action) => {
        state.pendingSigningIslamicAgreements.isLoading = false;
        state.pendingSigningIslamicAgreements.isError = true;
        state.pendingSigningIslamicAgreements.errorMessage = action.payload;
      });

    builder
      .addCase(getIslamicSignedAgreementsThunk.pending, (state) => {
        state.signedIslamicAgreements.isLoading = true;
      })
      .addCase(getIslamicSignedAgreementsThunk.fulfilled, (state, action) => {
        state.signedIslamicAgreements.isLoading = false;
        state.signedIslamicAgreements.data = {
          ...action.payload,
          data: action.payload.data.map((agreement: IslamicAgreement) => ({
            ...agreement,
            agreementStatus:
              agreement.agreementStatus === 'PENDING'
                ? 'PENDING_FOR_ATTESTATION'
                : agreement.agreementStatus,
          })),
        };
      })
      .addCase(getIslamicSignedAgreementsThunk.rejected, (state, action) => {
        state.signedIslamicAgreements.isLoading = false;
        state.signedIslamicAgreements.isError = true;
        state.signedIslamicAgreements.errorMessage = action.payload;
      });

    builder
      .addCase(getIslamicPendingEStampAgreementsThunk.pending, (state) => {
        state.pendingEStampIslamicAgreements.isLoading = true;
      })
      .addCase(getIslamicPendingEStampAgreementsThunk.fulfilled, (state, action) => {
        state.pendingEStampIslamicAgreements.isLoading = false;
        state.pendingEStampIslamicAgreements.data = {
          ...action.payload,
          data: action.payload.data.map((agreement: IslamicAgreement) => ({
            ...agreement,
            agreementStatus:
              agreement.agreementStatus === 'PENDING'
                ? 'PENDING_FOR_ATTESTATION'
                : agreement.agreementStatus,
          })),
        };
      })
      .addCase(getIslamicPendingEStampAgreementsThunk.rejected, (state, action) => {
        state.pendingEStampIslamicAgreements.isLoading = false;
        state.pendingEStampIslamicAgreements.isError = true;
        state.pendingEStampIslamicAgreements.errorMessage = action.payload;
      });

    builder
      .addCase(getIslamicInProgressEStampAgreementsThunk.pending, (state) => {
        state.inProgressEStampIslamicAgreements.isLoading = true;
      })
      .addCase(getIslamicInProgressEStampAgreementsThunk.fulfilled, (state, action) => {
        state.inProgressEStampIslamicAgreements.isLoading = false;
        state.inProgressEStampIslamicAgreements.data = {
          ...action.payload,
          data: action.payload.data.map((agreement: IslamicAgreement) => ({
            ...agreement,
            agreementStatus:
              agreement.agreementStatus === 'PENDING'
                ? 'PENDING_FOR_ATTESTATION'
                : agreement.agreementStatus,
          })),
        };
      })
      .addCase(getIslamicInProgressEStampAgreementsThunk.rejected, (state, action) => {
        state.inProgressEStampIslamicAgreements.isLoading = false;
        state.inProgressEStampIslamicAgreements.isError = true;
        state.inProgressEStampIslamicAgreements.errorMessage = action.payload;
      });

    builder
      .addCase(getIslamicUploadedEStampAgreementsThunk.pending, (state) => {
        state.uploadedEStampIslamicAgreements.isLoading = true;
      })
      .addCase(getIslamicUploadedEStampAgreementsThunk.fulfilled, (state, action) => {
        state.uploadedEStampIslamicAgreements.isLoading = false;
        state.uploadedEStampIslamicAgreements.data = {
          ...action.payload,
          data: action.payload.data.map((agreement: IslamicAgreement) => ({
            ...agreement,
            agreementStatus:
              agreement.agreementStatus === 'PENDING'
                ? 'PENDING_FOR_ATTESTATION'
                : agreement.agreementStatus,
          })),
        };
      })
      .addCase(getIslamicUploadedEStampAgreementsThunk.rejected, (state, action) => {
        state.uploadedEStampIslamicAgreements.isLoading = false;
        state.uploadedEStampIslamicAgreements.isError = true;
        state.uploadedEStampIslamicAgreements.errorMessage = action.payload;
      });

    builder
      .addCase(getAgreementDetailsThunk.pending, (state) => {
        state.agreementDetails.isLoading = true;
      })
      .addCase(getAgreementDetailsThunk.fulfilled, (state, action) => {
        state.agreementDetails.isLoading = false;
        state.agreementDetails.data = {
          ...action.payload,
          agreementStatus:
            action.payload.agreementStatus === 'PENDING'
              ? 'PENDING_FOR_ATTESTATION'
              : action.payload.agreementStatus,
        };
      })
      .addCase(getAgreementDetailsThunk.rejected, (state, action) => {
        state.agreementDetails.isLoading = false;
        state.agreementDetails.isError = true;
        state.agreementDetails.errorMessage = action.payload;
      });

    builder
      .addCase(getFilesByEntityIdThunk.pending, (state) => {
        state.agreementFiles.isLoading = true;
      })
      .addCase(getFilesByEntityIdThunk.fulfilled, (state, action) => {
        state.agreementFiles.isLoading = false;
        state.agreementFiles.data = groupBy(action.payload, (file: UploadedFile) =>
          file.fileType.name.toLowerCase()
        );

        // only keep the latest file for fileType signedagreemetnpdf
        if (
          state?.agreementFiles?.data?.signedagreementpdf &&
          state.agreementFiles.data.signedagreementpdf.length > 0
        ) {
          state.agreementFiles.data.signedagreementpdf = [
            state.agreementFiles.data.signedagreementpdf[
              state.agreementFiles.data.signedagreementpdf.length - 1
            ],
          ];
        }
      })
      .addCase(getFilesByEntityIdThunk.rejected, (state, action) => {
        state.agreementFiles.isLoading = false;
        state.agreementFiles.isError = true;
        state.agreementFiles.errorMessage = action.payload;
      });

    builder
      .addCase(getPendingTradingAgreementsThunk.pending, (state) => {
        state.pendingTradingAgreements.isLoading = true;
      })
      .addCase(getPendingTradingAgreementsThunk.fulfilled, (state, action) => {
        state.pendingTradingAgreements.isLoading = false;
        state.pendingTradingAgreements.data = action.payload;
      })
      .addCase(getPendingTradingAgreementsThunk.rejected, (state, action) => {
        state.pendingTradingAgreements.isLoading = false;
        state.pendingTradingAgreements.isError = true;
        state.pendingTradingAgreements.errorMessage = action.payload;
      });
  },
});

export const { agreementStateReset, agreementDetailsStateReset, setAgreementDetails } =
  islamicAgreementSlice.actions;

export default islamicAgreementSlice.reducer;
